// M05 /diagnosticos/modelos/:id/versoes/:version: manual hierarchy editor for a draft version (codes, labels,
// order, add/remove), publication with an impact note, and a read-only view of published versions.
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Alert, Button, ErrorState, Panel, Pill, Skeleton, TextArea, TextField } from "../../components/ui.tsx";
import { Modal } from "../../components/dialog.tsx";
import { BackLink, StickyActions } from "../../components/nav-controls.tsx";
import { useToast } from "../../components/toast.tsx";
import { ApiError, get, post, put } from "../../lib/api.ts";
import { useUnsavedGuard } from "../../lib/guard.tsx";
import { usePageTitle } from "../../lib/nav.tsx";
import { mBase, mKey, withInstitution, type MaturityInstitution, type VersionDetail } from "../../lib/maturity.ts";
import { MaturityLayout, WithInstitution } from "./MaturityLayout.tsx";
import { NotFoundPage } from "../NotFoundPage.tsx";
import { formatInstantBR } from "../../../shared/dates.ts";
import { MATURITY_LIMITS, newNodeId, publishErrors, questionsOf, type DefCategory, type DefSubdimension, type Definition } from "../../../shared/maturity/definition.ts";
import type { FieldError } from "../../../shared/api.ts";

export function TemplateVersionPage({ templateId, versionId }: { templateId: string; versionId: string }) {
  return <WithInstitution>{(inst) => <VersionBody institution={inst} templateId={templateId} versionId={versionId} />}</WithInstitution>;
}

function VersionBody({ institution, templateId, versionId }: { institution: MaturityInstitution; templateId: string; versionId: string }) {
  const key = mKey(institution.id, "template", templateId, versionId);
  const q = useQuery({ queryKey: key, queryFn: async () => (await get<{ data: VersionDetail }>(`${mBase(institution.id)}/templates/${templateId}/versions/${versionId}`)).data });
  usePageTitle("Modelo de questionário", q.data?.template_name);
  if (q.isPending) return <MaturityLayout><Skeleton lines={6} /></MaturityLayout>;
  if (q.isError) {
    if (q.error instanceof ApiError && q.error.status === 404) return <NotFoundPage />;
    return <MaturityLayout><ErrorState error={q.error} onRetry={() => void q.refetch()} /></MaturityLayout>;
  }
  return <Editor key={`${q.data.id}:${q.data.version}`} institution={institution} v={q.data} />;
}

type Move = (from: number, to: number) => void;
const moved = <T,>(list: T[], from: number, to: number) => {
  if (to < 0 || to >= list.length) return list;
  const out = list.slice();
  const [x] = out.splice(from, 1);
  out.splice(to, 0, x!);
  return out;
};

function RowTools({ index, count, onMove, onRemove, label }: { index: number; count: number; onMove: Move; onRemove: () => void; label: string }) {
  return (
    <span className="flex shrink-0 gap-1">
      <Button variant="ghost" className="px-2" aria-label={`Mover ${label} para cima`} disabled={index === 0} onClick={() => onMove(index, index - 1)}><ArrowUp className="size-4" aria-hidden /></Button>
      <Button variant="ghost" className="px-2" aria-label={`Mover ${label} para baixo`} disabled={index === count - 1} onClick={() => onMove(index, index + 1)}><ArrowDown className="size-4" aria-hidden /></Button>
      <Button variant="dangerGhost" className="px-2" aria-label={`Remover ${label}`} onClick={onRemove}><Trash2 className="size-4" aria-hidden /></Button>
    </span>
  );
}

function Editor({ institution, v }: { institution: MaturityInstitution; v: VersionDetail }) {
  const qc = useQueryClient();
  const toast = useToast();
  const draft = v.state === "draft";
  const [def, setDef] = useState<Definition>(() => structuredClone(v.definition));
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [conflict, setConflict] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const dirty = draft && JSON.stringify(def) !== JSON.stringify(v.definition);
  useUnsavedGuard(dirty);
  const local = useMemo(() => publishErrors(def), [def]);
  const path = `${mBase(institution.id)}/templates/${v.template_id}/versions/${v.id}`;
  const subs = def.dimension.subdimensions;

  const setSubs = (fn: (s: DefSubdimension[]) => DefSubdimension[]) => setDef((d) => ({ ...d, dimension: { ...d.dimension, subdimensions: fn(d.dimension.subdimensions) } }));
  const setSub = (si: number, fn: (s: DefSubdimension) => DefSubdimension) => setSubs((list) => list.map((s, i) => (i === si ? fn(s) : s)));
  const setCat = (si: number, ci: number, fn: (c: DefCategory) => DefCategory) => setSub(si, (s) => ({ ...s, categories: s.categories.map((c, i) => (i === ci ? fn(c) : c)) }));

  async function after(res: { data: VersionDetail }, msg: string) {
    qc.setQueryData(mKey(institution.id, "template", v.template_id, v.id), res.data);
    await qc.invalidateQueries({ queryKey: mKey(institution.id, "templates") });
    toast("success", msg);
  }
  function fail(e: unknown, fallback: string) {
    if (e instanceof ApiError && e.code === "VERSION_CONFLICT") setConflict(true);
    else if (e instanceof ApiError && e.fieldErrors.length) setErrors(e.fieldErrors);
    else toast("danger", e instanceof Error ? e.message : fallback);
  }
  async function save() {
    if (!dirty || busy) return;
    setBusy(true);
    setErrors([]);
    try {
      await after(await put<{ data: VersionDetail }>(path, { expected_version: v.version, definition: def }), "Rascunho do modelo salvo.");
    } catch (e) {
      fail(e, "Não foi possível salvar o modelo.");
    } finally {
      setBusy(false);
    }
  }
  async function publish() {
    setBusy(true);
    try {
      await after(await post<{ data: VersionDetail }>(`${path}/publish`, { expected_version: v.version }), "Versão publicada. Ela já pode ser escolhida em novas avaliações.");
      setPublishing(false);
    } catch (e) {
      setPublishing(false);
      fail(e, "Não foi possível publicar.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <MaturityLayout>
      <div className="mb-2"><BackLink fallback={withInstitution("/diagnosticos/modelos", institution.id)} label="Modelos" /></div>
      <header className="mb-5">
        <p className="eyebrow">{institution.name} · Modelo de questionário</p>
        <h1 id="page-title" tabIndex={-1} className="mt-1 flex flex-wrap items-center gap-2 text-[26px] leading-tight font-bold tracking-tight outline-none">
          {v.template_name} · versão {v.revision_number} {draft ? <Pill tone="gold">Rascunho</Pill> : <Pill>Publicada</Pill>}
        </h1>
        <p className="mt-1 text-sm text-muted">
          <span className="num">{questionsOf(def).length}</span> quesitos em <span className="num">{subs.length}</span> subdimensões · método {v.method_code}
          {v.published_at ? ` · publicada em ${formatInstantBR(v.published_at)}${v.published_by_name ? ` por ${v.published_by_name}` : ""}` : ""}
        </p>
      </header>
      {!draft && <Alert tone="info">Versão publicada: o conteúdo não muda, e as avaliações feitas com ela continuam com estas perguntas. Para alterar, crie uma nova versão a partir desta na lista de modelos.</Alert>}
      {conflict && <Alert tone="warn" title="Este modelo foi alterado por outro instrutor. Confira a versão atual antes de salvar."
        action={<Button variant="secondary" onClick={() => { setConflict(false); void qc.invalidateQueries({ queryKey: mKey(institution.id, "template", v.template_id, v.id) }); }}>Carregar versão atual (descarta suas alterações)</Button>} />}
      {errors.length > 0 && <Alert tone="danger" title="Revise o modelo"><ul className="list-disc pl-5">{errors.slice(0, 8).map((e) => <li key={e.path + e.message}>{e.message}</li>)}</ul></Alert>}

      <Panel className="mb-4">
        <p className="eyebrow">Dimensão</p>
        {draft ? (
          <div className="grid gap-x-4 sm:grid-cols-[8rem_1fr]">
            <TextField label="Código" maxLength={MATURITY_LIMITS.code} value={def.dimension.code} onChange={(e) => setDef((d) => ({ ...d, dimension: { ...d.dimension, code: e.target.value } }))} />
            <TextField label="Título" maxLength={MATURITY_LIMITS.nodeTitle} value={def.dimension.title} onChange={(e) => setDef((d) => ({ ...d, dimension: { ...d.dimension, title: e.target.value } }))} />
          </div>
        ) : <p className="mt-1 font-semibold">{def.dimension.code} · {def.dimension.title}</p>}
      </Panel>

      <ol className="space-y-4">
        {subs.map((s, si) => (
          <li key={s.id}>
            <Panel>
              <div className="flex items-start gap-2">
                <p className="eyebrow flex-1 pt-1">Subdimensão {si + 1}</p>
                {draft && <RowTools index={si} count={subs.length} label={`subdimensão ${s.code}`} onMove={(a, b) => setSubs((l) => moved(l, a, b))} onRemove={() => setSubs((l) => l.filter((_, i) => i !== si))} />}
              </div>
              {draft ? (
                <div className="grid gap-x-4 sm:grid-cols-[8rem_1fr]">
                  <TextField label="Código" maxLength={MATURITY_LIMITS.code} value={s.code} onChange={(e) => setSub(si, (x) => ({ ...x, code: e.target.value }))} />
                  <TextField label="Título" maxLength={MATURITY_LIMITS.nodeTitle} value={s.title} onChange={(e) => setSub(si, (x) => ({ ...x, title: e.target.value }))} />
                </div>
              ) : <h2 className="font-bold">{s.title}</h2>}
              <ol className="mt-2 space-y-3 border-l-2 border-line pl-3 sm:pl-4">
                {s.categories.map((c, ci) => (
                  <li key={c.id} className="rounded-ctl bg-paper p-3">
                    <div className="flex items-start gap-2">
                      <p className="eyebrow flex-1 pt-1">Categoria {ci + 1}</p>
                      {draft && <RowTools index={ci} count={s.categories.length} label={`categoria ${c.code}`} onMove={(a, b) => setSub(si, (x) => ({ ...x, categories: moved(x.categories, a, b) }))} onRemove={() => setSub(si, (x) => ({ ...x, categories: x.categories.filter((_, i) => i !== ci) }))} />}
                    </div>
                    {draft ? (
                      <div className="grid gap-x-4 sm:grid-cols-[8rem_1fr]">
                        <TextField label="Código" maxLength={MATURITY_LIMITS.code} value={c.code} onChange={(e) => setCat(si, ci, (x) => ({ ...x, code: e.target.value }))} />
                        <TextField label="Título" maxLength={MATURITY_LIMITS.nodeTitle} value={c.title} onChange={(e) => setCat(si, ci, (x) => ({ ...x, title: e.target.value }))} />
                      </div>
                    ) : <h3 className="font-semibold">{c.title}</h3>}
                    <ol className="mt-1 space-y-2">
                      {c.questions.map((qn, qi) => (
                        <li key={qn.id} className="rounded-ctl border border-line bg-surface px-3 py-2">
                          {draft ? (
                            <>
                              <div className="flex items-start gap-2">
                                <div className="w-32 [&>div]:my-1"><TextField label="Código do quesito" maxLength={MATURITY_LIMITS.code} value={qn.code} onChange={(e) => setCat(si, ci, (x) => ({ ...x, questions: x.questions.map((y, i) => (i === qi ? { ...y, code: e.target.value } : y)) }))} /></div>
                                <span className="flex-1" />
                                <RowTools index={qi} count={c.questions.length} label={`quesito ${qn.code}`} onMove={(a, b) => setCat(si, ci, (x) => ({ ...x, questions: moved(x.questions, a, b) }))} onRemove={() => setCat(si, ci, (x) => ({ ...x, questions: x.questions.filter((_, i) => i !== qi) }))} />
                              </div>
                              <div className="[&>div]:my-1"><TextArea label="Texto do quesito" maxLength={MATURITY_LIMITS.questionText} value={qn.text} onChange={(e) => setCat(si, ci, (x) => ({ ...x, questions: x.questions.map((y, i) => (i === qi ? { ...y, text: e.target.value } : y)) }))} /></div>
                            </>
                          ) : <p className="text-sm"><span className="eyebrow mr-2">{qn.code}</span>{qn.text}</p>}
                        </li>
                      ))}
                    </ol>
                    {draft && <Button variant="ghost" className="mt-1" onClick={() => setCat(si, ci, (x) => ({ ...x, questions: [...x.questions, { id: newNodeId("q"), code: `${x.code}${String.fromCharCode(65 + x.questions.length)}`, text: "" }] }))}><Plus className="size-4" aria-hidden />Adicionar quesito</Button>}
                  </li>
                ))}
              </ol>
              {draft && <Button variant="ghost" className="mt-2" onClick={() => setSub(si, (x) => ({ ...x, categories: [...x.categories, { id: newNodeId("cat"), code: "", title: "", questions: [] }] }))}><Plus className="size-4" aria-hidden />Adicionar categoria</Button>}
            </Panel>
          </li>
        ))}
      </ol>
      {draft && <Button variant="secondary" className="mt-4" onClick={() => setSubs((l) => [...l, { id: newNodeId("sub"), code: "", title: "", categories: [] }])}><Plus className="size-4" aria-hidden />Adicionar subdimensão</Button>}

      {draft && (
        <StickyActions dirty={dirty} onSave={() => void save()} summary={local.length ? <span className="text-warn-ink">{local.length} pendência(s) para publicar</span> : <span className="text-muted">Pronto para publicar</span>}>
          <Button variant="secondary" loading={busy && !publishing} disabled={!dirty} onClick={() => void save()}>Salvar rascunho</Button>
          <Button disabled={dirty || busy || local.length > 0} onClick={() => setPublishing(true)} title={dirty ? "Salve antes de publicar" : undefined}>Publicar versão</Button>
        </StickyActions>
      )}
      <Modal open={publishing} onOpenChange={(o) => !o && !busy && setPublishing(false)} title={`Publicar versão ${v.revision_number}`}
        footer={<><Button variant="secondary" disabled={busy} onClick={() => setPublishing(false)}>Voltar</Button><Button loading={busy} onClick={() => void publish()}>Publicar</Button></>}>
        <p className="text-sm">Depois de publicada, esta versão não pode mais ser alterada e fica disponível para novas avaliações.</p>
        <p className="mt-2 text-sm text-muted">Impacto: avaliações já criadas continuam com a versão em que foram criadas; nenhuma resposta é remapeada.</p>
      </Modal>
    </MaturityLayout>
  );
}
